import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { parseAsInteger, useQueryState } from "nuqs";
import React, { useEffect, useState } from "react";
import {
	getGlobalHeadersAsObject,
	getSearchTemplates,
	SearchTemplates as SearchTemplatesType,
	User,
} from "../../api";
import { sendTodoPing } from "../../api/celtask";
import useDynamicTabTitle from "../dynamic-tab-title";
import { useGlobalState } from "../globalState";
import { PageLoader } from "../page-loader";
import { UserGroupTabSelector } from "../user-group-tab-selector";
import { AllNeedsAttention } from "./AllNeedsAttention";
import { AttentionBadge } from "./AttentionBadge";
import { BaseTodoBox } from "./BaseTodoBox";
import { ExpiredReminders } from "./ExpiredReminders";
import { ExpiredRemindersProspect } from "./ExpiredRemindersProspect";
import { Followups } from "./Followups";
import { PersonalTodos } from "./personal-todos";
import { RecruitAssignments } from "./RecruitmentAssignments";
import { StaleConversations, StaleConversationsLeads } from "./StaleConversations";
import { TodoTag } from "./TodoTag";
import { setClassNamesByCount } from "./utils";

export const Todo = () => {
	useDynamicTabTitle("Todo");
	const [searchTemplates, setSearchTemplates] = useState<SearchTemplatesType>();
	const [viewUser, setViewUser] = useState<User>(null);
	const { currentUser, users, loading } = useGlobalState();
	const [userParam, setUserParam] = useQueryState("user", parseAsInteger);
	const [groupParam, setGroupParam] = useQueryState("group", parseAsInteger);

	type SearchTemplateByUser = {
		[key: string]: Array<{
			name: string;
			url: string;
		}>;
	};

	const [templates, setTemplates] = useState<SearchTemplatesType>();
	const isInitialized = React.useRef(false);

	useEffect(() => {
		if (!viewUser || loading || !searchTemplates) return;
		let isMounted = true;

		if (isMounted) {
			const searchTemplatesByUser: SearchTemplateByUser = Object.keys(
				searchTemplates || []
			).reduce((acc, curr) => {
				let matchesToUser = false;
				users &&
					users.forEach((user) => {
						if (curr.includes(user.username)) {
							if (!acc[user.id]) acc[user.id] = [];
							acc[user.id].push({
								name: curr,
								url: searchTemplates[curr],
							});
							matchesToUser = true;
						}
					});

				if (!matchesToUser) {
					if (!acc["all"]) acc["all"] = [];
					acc["all"].push({ name: curr, url: searchTemplates[curr] });
				}

				return acc;
			}, {});

			const toFetch = [
				...(searchTemplatesByUser[viewUser.id] ?? []),
				...(searchTemplatesByUser["all"] ?? []),
			];
			console.debug({
				viewUser,
				currentUser,
				toFetch,
				searchTemplatesByUser,
				searchTemplates,
			});

			let templateToProcess = templates;
			console.debug("Before Init", {
				templateToProcess,
				isInitialized: isInitialized.current,
			});
			if (!isInitialized.current) {
				const defaultTemplates = Object.keys(searchTemplates).reduce(
					(acc, curr) => {
						acc[curr] = { count: "*", url: searchTemplates[curr] };
						return acc;
					},
					{}
				) as SearchTemplatesType;
				setTemplates(defaultTemplates);
				templateToProcess = defaultTemplates;
				isInitialized.current = true;
				console.debug("After Init", {
					templateToProcess,
					isInitialized: isInitialized.current,
				});
			}
			toFetch.forEach((searchTemplate) => {
				if (templateToProcess[searchTemplate.name].count !== "*") return;

				fetch(`/api/v1/search_template_count/${searchTemplate.name}/`, {
					method: "GET",
					headers: getGlobalHeadersAsObject(),
				})
					.then((data) => data.json())
					.then((count) => {
						setTemplates((prevTemplates) => {
							return {
								...prevTemplates,
								[searchTemplate.name]: {
									url: searchTemplates[searchTemplate.name],
									count: count.hits,
								},
							};
						});
					});
			});
		}

		return () => {
			isMounted = false;
		};
	}, [viewUser, currentUser, users, loading, searchTemplates]);

	useEffect(() => {
		console.log(
			`[Todo] Mounted, fetching search_templates at ${performance.now().toFixed(0)}ms`
		);
		sendTodoPing();
		getSearchTemplates().then((templates) => {
			setSearchTemplates(templates);
		});
	}, []);

	//Init current user
	useEffect(() => {
		if (!viewUser && userParam === null && currentUser) {
			setViewUser(currentUser);
			setUserParam(currentUser.id);
			setGroupParam(currentUser.userprofile?.up_group?.id ?? null);
		} else if (users && users.length > 0 && userParam !== null) {
			const userObj = users.find((el) => el.id === userParam);
			if (userObj && userObj !== viewUser) {
				setViewUser(userObj);
				setGroupParam(userObj.userprofile?.up_group?.id ?? null);
			}
		}
	}, [currentUser, users]);

	const switchUser = (user: User) => {
		setViewUser(user);
		setUserParam(user.id);
		setGroupParam(user.userprofile?.up_group?.id ?? null);
	};

	const mainRender = () => {
		if (
			!templates ||
			!Object.keys(templates)
				.map((key) => key.includes(viewUser.username))
				.some((el) => el === true)
		)
			return;
		let readonly = currentUser.id != viewUser.id;
		let config = viewUser.userprofile.config;

		return (
			<Box
				p={3}
				pt={0}
				sx={{
					width: "100%",
				}}
				data-component="todo-main-content"
			>
				<Grid container spacing={3} data-component="todo-sections-grid">
					{!readonly && currentUser.userprofile.show_personal_todos ? (
						<PersonalTodos />
					) : null}

					{config.todo.show_needs_attention ? (
						<AllNeedsAttention
							activeUser={viewUser}
							needsAttention={
								templates[`all_needs_attention_${viewUser.username}`]
							}
							title="Respond to all who said something"
							tag={`${viewUser.username}-needs-attention`}
						/>
					) : null}

					{config.todo.show_pitch_needs_attention ? (
						<AllNeedsAttention
							activeUser={viewUser}
							needsAttention={
								templates[`pitch_needs_attention_${viewUser.username}`]
							}
							title="Pitch Responses that need Attention"
							tag={`${viewUser.username}-pitch-response`}
						/>
					) : null}

					{config.todo.show_stale_conversation_leads ? (
						<StaleConversationsLeads
							staleConversations={
								templates[
									`stale_conversations_leads_${viewUser.username}`
								]
							}
						/>
					) : null}

					{config.todo.show_stale_email_conversation_leads ? (
						<BaseTodoBox
							className={setClassNamesByCount(
								templates[`stale_email_leads_${viewUser.username}`]
									.count
							)}
							title={
								<AttentionBadge
									count={
										templates[
											"stale_email_leads_" + viewUser.username
										].count
									}
									title="Stale Email conversations that are leads"
									readOnly={false}
									url={
										templates[
											`stale_email_leads_${viewUser.username}`
										].url
									}
								/>
							}
							description={
								<Typography
									variant="body1"
									component="div"
									color="textPrimary"
									data-component="stale-email-description"
								>
									Set <b>reminder</b> or set one of the tags{" "}
									<TodoTag>artemis-tackar-nej</TodoTag>{" "}
									<TodoTag>artemis-bokad-intervju</TodoTag> to remove
									person from list
								</Typography>
							}
						/>
					) : null}

					{config.todo.show_expired_reminders_prospect ? (
						<ExpiredRemindersProspect
							expiredRemindersProspect={
								templates[
									"expired_reminder_prospect_" + viewUser.username
								]
							}
						/>
					) : null}

					{config.todo.show_expired_reminders ? (
						<ExpiredReminders
							expiredRemindersCorona={
								templates["expired_reminder_" + viewUser.username]
							}
						/>
					) : null}

					{config.todo.show_match_connectable ? (
						<RecruitAssignments
							readOnly={readonly}
							activeUser={viewUser}
							title={
								<Box
									component="span"
									className="text-primary"
									data-component="roles-title"
								>
									Roles
								</Box>
							}
						/>
					) : null}

					{config.todo.show_stale_conversions ? (
						<StaleConversations
							staleConversationsCorona={
								templates[
									"stale_conversations_corona_" + viewUser.username
								]
							}
						/>
					) : null}

					{config.todo.show_followups ? (
						<Followups
							readOnly={readonly}
							activeUser={viewUser}
							followups={
								templates["others_need_attention_" + viewUser.username]
							}
						/>
					) : null}
				</Grid>
			</Box>
		);
	};

	return (
		<Box data-component="todo-page">
			<CssBaseline />
			<UserGroupTabSelector
				users={users}
				selectedUserId={viewUser?.id ?? null}
				groupParam={groupParam}
				onSelectUser={switchUser}
				dataComponentPrefix="todo"
			/>
			<Grid container spacing={1} sx={{ pt: 3 }} data-component="todo-container">
				{viewUser === undefined || templates === undefined ? (
					<PageLoader />
				) : (
					mainRender()
				)}
			</Grid>
		</Box>
	);
};
